import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Copy, Wand2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One-click sample inputs so visitors never face a blank box.
const EXAMPLES = {
  "email-polite-no":
    "Hi! We'd love you to speak at our online summit next month. It's unpaid, but it's great exposure — could you also prepare a 30-minute workshop and join the panel afterwards?",
  "summarise-notes":
    "monday standup — launch slipped to thursday bc of the payment bug, sara owns the fix. marketing wants new screenshots by wed. budget approved for one freelancer. someone needs to email beta users about the delay. ali is on holiday next week.",
  "caption-from-idea":
    "I just finished a 30-day challenge of writing every morning before checking my phone.",
};

const PLACEHOLDERS = {
  "email-polite-no": "Paste the email or request you want to politely decline…",
  "summarise-notes": "Paste your messy notes — typos and fragments are fine…",
  "caption-from-idea": "Describe your idea in a sentence or two…",
};

const Step = ({ n, label }) => (
  <div className="flex items-center gap-2">
    <span className="w-5 h-5 rounded-full bg-[#2563EB] text-white font-mono text-[10px] flex items-center justify-center shrink-0">
      {n}
    </span>
    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#111111]/55">
      {label}
    </span>
  </div>
);

const Playground = () => {
  const [prompts, setPrompts] = useState([]);
  const [active, setActive] = useState(null);
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [dailyLimit, setDailyLimit] = useState(3);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/playground/prompts`)
      .then(({ data }) => {
        if (cancelled) return;
        setPrompts(data.prompts || []);
        setDailyLimit(data.daily_limit || 3);
        if (data.prompts?.length) setActive(data.prompts[0].id);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const fillExample = () => {
    if (active && EXAMPLES[active]) {
      setInput(EXAMPLES[active]);
      setOutput("");
    }
  };

  const copyOutput = () => {
    navigator.clipboard.writeText(output);
    toast.success("Copied.");
  };

  const run = async () => {
    if (!active || !input.trim()) {
      toast.error("Add some text first — or click “Try an example”.");
      return;
    }
    setLoading(true);
    setOutput("");
    try {
      const { data } = await axios.post(`${API}/playground/run`, {
        prompt_id: active,
        user_input: input.trim(),
      });
      setOutput(data.output);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Playground failed.");
    } finally {
      setLoading(false);
    }
  };

  if (!prompts.length) return null;

  return (
    <section
      data-testid="section-playground"
      id="try"
      className="bg-white border-y border-[#E7E7E2]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="overline">— Try it now, free</div>
        <h2 className="mt-5 font-sora text-[32px] sm:text-[44px] lg:text-[52px] font-bold text-[#111111] tracking-tight leading-[1.05]">
          See a real prompt in action.
        </h2>
        <p className="mt-5 max-w-xl font-manrope text-[16px] text-[#111111]/65">
          This runs a real prompt from the kit on your text. No signup, no
          card — just pick a task and go. ({dailyLimit} free runs a day.)
        </p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-px bg-[#E7E7E2] border border-[#E7E7E2] rounded-2xl overflow-hidden">
          {/* Left: input */}
          <div className="lg:col-span-6 bg-white p-7 md:p-8">
            <Step n="1" label="Pick a task" />
            <div className="mt-3 flex flex-wrap gap-2">
              {prompts.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    setActive(p.id);
                    setInput("");
                    setOutput("");
                  }}
                  data-testid={`playground-prompt-${p.id}`}
                  className={`px-3.5 py-2 rounded-full font-manrope text-[13px] border transition-colors ${
                    active === p.id
                      ? "bg-[#111111] text-white border-[#111111]"
                      : "bg-white text-[#111111]/75 border-[#E7E7E2] hover:border-[#111111]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="mt-7">
              <div className="flex items-center justify-between mb-2">
                <Step n="2" label="Your text" />
                <button
                  type="button"
                  onClick={fillExample}
                  data-testid="playground-example-btn"
                  className="inline-flex items-center gap-1.5 font-manrope text-[12.5px] font-medium text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
                >
                  <Wand2 size={12} />
                  Try an example
                </button>
              </div>
              <textarea
                data-testid="playground-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                rows={6}
                maxLength={2000}
                placeholder={PLACEHOLDERS[active] || "Paste your text here…"}
                className="w-full bg-white border border-[#E7E7E2] rounded-lg px-4 py-3 text-[15px] text-[#111111] placeholder:text-[#111111]/40 font-manrope focus:outline-none focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 transition-all resize-none"
              />
            </div>

            <button
              onClick={run}
              disabled={loading}
              data-testid="playground-run"
              className="mt-5 inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white font-manrope font-medium text-[15px] px-6 py-3 rounded-lg transition-colors"
            >
              <Sparkles size={15} />
              {loading ? "Writing…" : "Run it"}
            </button>
          </div>

          {/* Right: output */}
          <div className="lg:col-span-6 bg-[#F7F7F4] p-7 md:p-8 flex flex-col min-h-[280px]">
            <div className="flex items-center justify-between">
              <Step n="3" label="Result" />
              {output && (
                <button
                  type="button"
                  onClick={copyOutput}
                  data-testid="playground-copy"
                  className="inline-flex items-center gap-1.5 font-manrope text-[12.5px] font-medium text-[#111111]/60 hover:text-[#2563EB] transition-colors"
                >
                  <Copy size={12} />
                  Copy
                </button>
              )}
            </div>
            <div
              data-testid="playground-output"
              className="mt-4 flex-1 font-manrope text-[15px] md:text-[16px] leading-[1.7] text-[#111111] whitespace-pre-wrap"
            >
              {loading ? (
                <span className="text-[#111111]/45">Writing your result…</span>
              ) : (
                output || (
                  <span className="text-[#111111]/45">
                    Your result appears here in a few seconds. Not sure what to
                    type? Hit “Try an example” and then “Run it”.
                  </span>
                )
              )}
            </div>
            {output && (
              <p className="mt-6 font-manrope text-sm text-[#111111]/55">
                This came from 1 of the 100 prompts inside the kit.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Playground;
